import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { CommandBase, standardCommand } from '../../../core/model/command';
import { Money } from '../../../core/model/money';
import { LanguageService } from '../../../core/service/language.service';
import { CompanyMortgageService } from '../../service/company-mortgage.service';
import { CompanyMortgage } from '../../model/company-mortgage';
import { CreditInstituteService } from '../../service/credit-institute.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { companyMortgageCommand } from '../../command/company-mortgage.command';
import { CompanyMortgageAmortization } from '../../model/company-mortgage-amortization';
import { AddAmortizationCommandHandler } from '../../service/command/add-amortization-command-handler.service';
import { CompanyMortgageFormCommandHandlerResolver } from '../../service/command/company-mortgage-command-handler-resolver.service';

@Component({
    selector: 'app-company-mortgage-form',
    templateUrl: './company-mortgage-form.component.html',
    styleUrls: ['./company-mortgage-form.component.scss'],
    providers: [
        { provide: DataService, useExisting: CompanyMortgageService },
        DeleteFormCommandHandler,
        CompanyMortgageFormCommandHandlerResolver,
        AddAmortizationCommandHandler,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyMortgageFormComponent extends FormComponentBase<CompanyMortgage> {
  constructor(
    private _dataService: CompanyMortgageService,
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _languageService: LanguageService,
    private _route: ActivatedRoute,
    creditInstituteService: CreditInstituteService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CompanyMortgageFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    companyService: CompanyService
  ) {
    super(_route, _dataService, messageService, navigation, commandDispatcher, handlerResolver);
    this.creditInstituteService = creditInstituteService;
    this.companyService = companyService;
    this.addMenuItemDefinition('MORTGAGE.COMMAND.REGISTER_AMORTIZATION', companyMortgageCommand.addAmortization);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }
  creditInstituteService: CreditInstituteService;
  protected createModel(): CompanyMortgage {
    return new CompanyMortgage();
  }

  executedWithResult(result: boolean, command: CommandBase) {
    super.executedWithResult(result, command);
    if (result && command.id === companyMortgageCommand.addAmortization) {
      this.form.controls['balance'].setValue(this.dataContext.balance);
      this.AddSubscription(
        this._dataService
          .getAmortizations(this.dataContext.id)
          .subscribe(res => (this.amortizations = this._fb.array(res.map(r => this.getAmortizationFormGroup(r)))))
      );
    }
  }

  private getAmortizationFormGroup(amortization: CompanyMortgageAmortization): FormGroup {
    return this._fb.group({
      amount: this._cf.createReadonly('COMMON.AMOUNT', amortization.amount),
      paymentDate: this._cf.createReadonly('MORTGAGE.AMORTIZATION_DATE', amortization.paymentDate),
    });
  }

  companyService: CompanyService;
  amortizations: FormArray;
  protected getFormGroup(): FormGroup {
    this.dataContext.amortizations.sort(
      (a, b) =>
        new Date(b.paymentDate.year, b.paymentDate.month - 1, b.paymentDate.day).getTime() -
        new Date(a.paymentDate.year, a.paymentDate.month - 1, a.paymentDate.day).getTime()
    );
    this.amortizations = this._fb.array(this.dataContext.amortizations.map(a => this.getAmortizationFormGroup(a)));

    let group = this._fb.group({
      id: this._cf.createHidden(this.dataContext.id),
      companyId: this._cf.createMandatory('COMMON.COMPANY', this.dataContext.companyId),
      creditInstituteId: this._cf.createMandatory('MORTGAGE.CREDIT_INSTITUTE', this.dataContext.creditInstituteId),
      initialAmount: this._cf.createMandatory('MORTGAGE.INITIAL_AMOUNT', this.dataContext.initialAmount),
      balance: this._cf.createMandatory('MORTGAGE.BALANCE', this.dataContext.balance),
      name: this._cf.createMandatory('MORTGAGE.COMPANY_MORTGAGE_NAME', this.dataContext.name),
      currentInterestRate: this._cf.create('MORTGAGE.CURRENT_INTEREST_RATE', this.dataContext.currentInterestRate),
      nextInterestRateChangeDate: this._cf.create('MORTGAGE.NEXT_INTEREST_CHANGE_DATE', this.dataContext.nextInterestRateChangeDate),
    });

    return group;
  }

  onCompanySelected(item: AutoCompleteSelectionItem) {
    if (!this.form.controls['initialAmount'].value || item.data.currencyCode !== this.form.controls['initialAmount'].value.currencyCode) {
      this.form.controls['initialAmount'].setValue(new Money(item.data.currencyCode, null));
      this.form.controls['balance'].setValue(new Money(item.data.currencyCode, null));
    }
  }
}
